import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link,
} from '@mui/material';
import { FileDownload, Close } from '@mui/icons-material';
import { POSTMORTEM } from '../config/api';

interface WeekData {
  weekLabel: string;
  closed: number;
  openWithinDue: number;
}

interface PostmortemTrendData {
  portfolioPlatform: string;
  weeks: WeekData[];
  totalOpen: number;
  openDueWithin5Days: number;
  openDue6To8Days: number;
  openPastDueDate: number;
}

interface PostmortemTrendsProps {
  selectedPortfolio?: string;
  selectedPlatform?: string;
}

const PostmortemTrends: React.FC<PostmortemTrendsProps> = ({ 
  selectedPortfolio,
  selectedPlatform 
}) => {
  const [trendData, setTrendData] = useState<PostmortemTrendData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weeks, setWeeks] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<any[]>([]);
  const [dialogTitle, setDialogTitle] = useState('');
  const [allPostmortemsData, setAllPostmortemsData] = useState<any[]>([]);
  const [selectedWeeks, setSelectedWeeks] = useState<number>(2);

  useEffect(() => {
    fetchPostmortemTrends();
  }, [selectedPortfolio, selectedPlatform, selectedWeeks]);

  const fetchPostmortemTrends = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Fetching postmortem trends data from Maersk API...');
      
      const response = await authFetch(POSTMORTEM.ALL_REPORTS);
      
      if (!response.ok) {
        throw new Error('Failed to fetch postmortem trends data');
      }

      const allPostmortems = await response.json();
      console.log('✅ Postmortem data received:', allPostmortems.length);

      // Process data to calculate trends
      const processedData = processPostmortemData(allPostmortems);
      setTrendData(processedData);
      
    } catch (err) {
      console.error('Error fetching postmortem trends:', err);
      setError('Failed to load postmortem trends data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processPostmortemData = (postmortems: any[]): PostmortemTrendData[] => {
    // Filter for P1/P2 priority incidents only AND created on or after Jan 1, 2025
    const jan1_2025 = new Date('2025-01-01T00:00:00');
    const filteredPostmortems = postmortems.filter((pm: any) => {
      const isPriorityMatch = pm.priority === '1' || pm.priority === '2';
      const createdDate = pm.createdOn ? new Date(pm.createdOn) : null;
      const isAfterJan2025 = createdDate ? createdDate >= jan1_2025 : false;
      return isPriorityMatch && isAfterJan2025;
    });

    // Apply portfolio and platform filters if provided
    let scopedPostmortems = filteredPostmortems;
    if (selectedPortfolio && selectedPortfolio !== 'all') {
      scopedPostmortems = scopedPostmortems.filter((pm: any) => 
        pm.causedBy_portfolio === selectedPortfolio
      );
    }
    if (selectedPlatform && selectedPlatform !== 'all') {
      scopedPostmortems = scopedPostmortems.filter((pm: any) => 
        pm.causedBy_platform === selectedPlatform
      );
    }

    // Store all postmortems for dialog use
    setAllPostmortemsData(scopedPostmortems);

    // Get current date and calculate week ranges
    const now = new Date();
    const currentWeekNumber = getWeekNumber(now);
    const currentYear = now.getFullYear();
    
    // Generate last N completed weeks data based on selectedWeeks (e.g., 2, 3, or 4 weeks)
    // Start from last week (currentWeekNumber - 1) to exclude current incomplete week
    const weekRanges: Array<{ weekLabel: string; weekNumber: number; year: number }> = [];
    for (let i = selectedWeeks; i >= 1; i--) {
      const weekNum = currentWeekNumber - i;
      weekRanges.push({
        weekLabel: `Week ${String(weekNum).padStart(2, '0')}`,
        weekNumber: weekNum,
        year: currentYear
      });
    }
    
    setWeeks(weekRanges.map(w => w.weekLabel));

    // Group postmortems by Portfolio (case-insensitive)
    const groupedData: { [key: string]: any[] } = {};
    
    scopedPostmortems.forEach((pm: any) => {
      const portfolioName = pm.causedBy_portfolio || 'Portfolio not defined';
      // Normalize to title case for consistent grouping
      const key = portfolioName === 'Portfolio not defined' 
        ? portfolioName 
        : portfolioName.split(' ').map((word: string) => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ');
      
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(pm);
    });

    // Calculate trends for each group
    const trendsData: PostmortemTrendData[] = Object.keys(groupedData).map(key => {
      const postmortemsInGroup = groupedData[key];
      
      // Calculate weekly data
      const weeklyData: WeekData[] = weekRanges.map(weekRange => {
        // For "Closed" column: Find postmortems that were closed in this specific week
        // Closed date = createdOn + duration (durationSRE or durationLT)
        const closed = postmortemsInGroup.filter((pm: any) => {
          const status = pm.incidentStatus?.toUpperCase();
          if (status !== 'DONE' && status !== 'LTREVIEW' && status !== 'COMPLETED' && status !== 'CLOSED') {
            return false;
          }
          
          const closedDate = getClosedDate(pm);
          if (!closedDate) return false;
          
          const closedWeekNum = getWeekNumber(closedDate);
          const closedYear = closedDate.getFullYear();
          
          return closedWeekNum === weekRange.weekNumber && closedYear === weekRange.year;
        }).length;

        // For "Open Post-Mortem within due date": Find postmortems created in this week that are still open
        const weekPostmortems = postmortemsInGroup.filter((pm: any) => {
          if (!pm.createdOn) return false;
          const pmDate = new Date(pm.createdOn);
          const pmWeekNum = getWeekNumber(pmDate);
          const pmYear = pmDate.getFullYear();
          return pmWeekNum === weekRange.weekNumber && pmYear === weekRange.year;
        });

        const openWithinDue = weekPostmortems.filter((pm: any) => {
          const status = pm.incidentStatus?.toUpperCase();
          return status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED' && status !== 'INCOMPLETE' && status !== 'LTREVIEW';
        }).length;

        return {
          weekLabel: weekRange.weekLabel,
          closed,
          openWithinDue
        };
      });

      // Calculate current open postmortems
      const currentOpenPostmortems = postmortemsInGroup.filter((pm: any) => {
        const status = pm.incidentStatus?.toUpperCase();
        return status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED' && status !== 'INCOMPLETE' && status !== 'LTREVIEW';
      });

      const totalOpen = currentOpenPostmortems.length;

      // Calculate due date categories (based on days since creation and 8-day SLA)
      // SLA = 8 days from creation
      const today = new Date();
      let openDueWithin5Days = 0;  // Will breach within 5 days (days 4-8 since creation)
      let openDue6To8Days = 0;     // Will breach after 5 days (days 0-3 since creation)
      let openPastDueDate = 0;     // Already breached SLA (> 8 days since creation)

      currentOpenPostmortems.forEach((pm: any) => {
        if (!pm.createdOn) return;
        
        const createdDate = new Date(pm.createdOn);
        const daysSinceCreation = Math.floor((today.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        const daysUntilBreach = 8 - daysSinceCreation; // Days remaining until 8-day SLA breach
        
        if (daysSinceCreation > 8) {
          // Already breached 8-day SLA (> 8 days old)
          openPastDueDate++;
        } else if (daysUntilBreach <= 5 && daysUntilBreach >= 0) {
          // Will breach within next 5 days (4-8 days old: 0-5 days until breach)
          openDueWithin5Days++;
        } else if (daysUntilBreach > 5) {
          // Will breach after 5 days (0-3 days old: 6-8 days until breach)
          openDue6To8Days++;
        }
      });

      return {
        portfolioPlatform: key,
        weeks: weeklyData,
        totalOpen,
        openDueWithin5Days,
        openDue6To8Days,
        openPastDueDate
      };
    });

    // Sort by portfolio name alphabetically
    trendsData.sort((a, b) => {
      return a.portfolioPlatform.localeCompare(b.portfolioPlatform);
    });

    // Calculate Grand Total row
    const grandTotal: PostmortemTrendData = {
      portfolioPlatform: 'Grand Total',
      weeks: weekRanges.map((weekRange, index) => ({
        weekLabel: weekRange.weekLabel,
        closed: trendsData.reduce((sum, data) => sum + (data.weeks[index]?.closed || 0), 0),
        openWithinDue: trendsData.reduce((sum, data) => sum + (data.weeks[index]?.openWithinDue || 0), 0)
      })),
      totalOpen: trendsData.reduce((sum, data) => sum + data.totalOpen, 0),
      openDueWithin5Days: trendsData.reduce((sum, data) => sum + data.openDueWithin5Days, 0),
      openDue6To8Days: trendsData.reduce((sum, data) => sum + data.openDue6To8Days, 0),
      openPastDueDate: trendsData.reduce((sum, data) => sum + data.openPastDueDate, 0)
    };

    return [...trendsData, grandTotal];
  };

  // Helper function to get ISO week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Helper function to parse duration string and return total days
  const parseDuration = (durationStr: string): number => {
    if (!durationStr) return 0;
    
    try {
      // Parse format like "213 days, 02 hours, 02 minutes" or "47 days, 21 hours, 45 minutes"
      const daysMatch = durationStr.match(/(\d+)\s*days?/i);
      const hoursMatch = durationStr.match(/(\d+)\s*hours?/i);
      const minutesMatch = durationStr.match(/(\d+)\s*minutes?/i);
      
      const days = daysMatch ? parseInt(daysMatch[1]) : 0;
      const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
      
      // Convert everything to days (for week calculation, hours and minutes are less significant)
      return days + (hours / 24) + (minutes / 1440);
    } catch (error) {
      console.error('Error parsing duration:', durationStr, error);
      return 0;
    }
  };

  // Helper function to calculate closed date (createdOn + durationSRE/durationLT)
  const getClosedDate = (pm: any): Date | null => {
    if (!pm.createdOn) return null;
    
    const status = pm.incidentStatus?.toUpperCase();
    const isClosed = status === 'DONE' || status === 'LTREVIEW' || status === 'COMPLETED' || status === 'CLOSED';
    
    if (!isClosed) return null;
    
    try {
      const createdDate = new Date(pm.createdOn);
      
      // For LTREVIEW status, use durationLT; for DONE, use durationSRE
      let durationStr = '';
      if (status === 'LTREVIEW' && pm.durationLT) {
        durationStr = pm.durationLT;
      } else if (status === 'DONE' && pm.durationSRE) {
        durationStr = pm.durationSRE;
      } else if (pm.durationSRE) {
        durationStr = pm.durationSRE;
      } else if (pm.durationLT) {
        durationStr = pm.durationLT;
      }
      
      if (!durationStr) return null;
      
      const durationDays = parseDuration(durationStr);
      
      // Add duration to created date
      const closedDate = new Date(createdDate);
      closedDate.setDate(closedDate.getDate() + Math.floor(durationDays));
      
      return closedDate;
    } catch (error) {
      console.error('Error calculating closed date for PM:', pm.customID, error);
      return null;
    }
  };

  // Handle click on counts to show detail dialog
  const handleCountClick = (portfolio: string, weekIndex: number, type: 'closed' | 'open' | 'dueWithin5' | 'due6To8' | 'pastDue') => {
    if (portfolio === 'Grand Total') return; // Don't show details for grand total
    
    const now = new Date();
    const currentWeekNumber = getWeekNumber(now);
    const currentYear = now.getFullYear();
    const weekNum = currentWeekNumber - (selectedWeeks - weekIndex); // Calculate actual week number based on selected weeks
    
    let filteredData: any[] = [];
    let title = '';
    
    // Filter postmortems for the specific portfolio (case-insensitive comparison)
    const portfolioPostmortems = allPostmortemsData.filter(pm => {
      if (portfolio === 'Portfolio not defined') {
        return !pm.causedBy_portfolio || pm.causedBy_portfolio.trim() === '';
      }
      // Normalize both sides for comparison
      const pmPortfolio = pm.causedBy_portfolio || '';
      const normalizedPmPortfolio = pmPortfolio.split(' ').map((word: string) => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      return normalizedPmPortfolio === portfolio;
    });
    
    if (type === 'closed' || type === 'open') {
      if (type === 'closed') {
        // Filter by closed week (createdOn + duration falls in this week)
        filteredData = portfolioPostmortems.filter((pm: any) => {
          const status = pm.incidentStatus?.toUpperCase();
          if (status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED' && status !== 'LTREVIEW') {
            return false;
          }
          
          const closedDate = getClosedDate(pm);
          if (!closedDate) return false;
          
          const closedWeekNum = getWeekNumber(closedDate);
          const closedYear = closedDate.getFullYear();
          
          return closedWeekNum === weekNum && closedYear === currentYear;
        });
      } else {
        // Filter by created week for open postmortems
        filteredData = portfolioPostmortems.filter((pm: any) => {
          if (!pm.createdOn) return false;
          const pmDate = new Date(pm.createdOn);
          const pmWeekNum = getWeekNumber(pmDate);
          const pmYear = pmDate.getFullYear();
          
          if (pmWeekNum !== weekNum || pmYear !== currentYear) return false;
          
          const status = pm.incidentStatus?.toUpperCase();
          return status !== 'DONE' && status !== 'COMPLETED' && status !== 'CLOSED' && status !== 'INCOMPLETE' && status !== 'LTREVIEW';
        });
      }
      
      const weekLabel = `Week ${String(weekNum).padStart(2, '0')}`;
      title = `${portfolio} - ${weekLabel} - ${type === 'closed' ? 'Closed' : 'Open'} Postmortems`;
    } else {
      // Filter by due date categories (based on 8-day SLA)
      const today = new Date();
      filteredData = portfolioPostmortems.filter((pm: any) => {
        const status = pm.incidentStatus?.toUpperCase();
        if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED' || status === 'INCOMPLETE' || status === 'LTREVIEW') {
          return false;
        }
        
        if (!pm.createdOn) return false;
        const createdDate = new Date(pm.createdOn);
        const daysSinceCreation = Math.floor((today.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
        const daysUntilBreach = 8 - daysSinceCreation;
        
        if (type === 'dueWithin5') {
          // Will breach within 5 days (4-8 days old: 0-5 days until breach)
          return daysSinceCreation <= 8 && daysUntilBreach <= 5 && daysUntilBreach >= 0;
        } else if (type === 'due6To8') {
          // Will breach after 5 days (0-3 days old: 6-8 days until breach)
          return daysUntilBreach > 5;
        } else if (type === 'pastDue') {
          // Already breached (> 8 days)
          return daysSinceCreation > 8;
        }
        return false;
      });
      
      let typeLabel = '';
      if (type === 'dueWithin5') {
        typeLabel = 'Due Within 5 Days';
      } else if (type === 'due6To8') {
        typeLabel = 'Due After 5 Days (6-8 Days)';
      } else if (type === 'pastDue') {
        typeLabel = 'Past Due Date (> 8 Days)';
      }
      title = `${portfolio} - ${typeLabel} Postmortems`;
    }
    
    setDialogData(filteredData);
    setDialogTitle(title);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogData([]);
    setDialogTitle('');
  };

  const handleExportToCSV = () => {
    try {
      // Prepare CSV headers
      const headers = [
        'Portfolio/Platform',
        ...weeks.map(w => `${w} - Closed`),
        ...weeks.map(w => `${w} - Open Within Due`),
        'Total Open',
        'Open PM - SLA Breach Within 5 Days',
        'Open PM - SLA Breach After 5 Days',
        'Open PM - SLA Breached (> 8 Days)'
      ];

      // Prepare CSV rows
      const rows = trendData.map(data => [
        data.portfolioPlatform,
        ...data.weeks.map(w => w.closed),
        ...data.weeks.map(w => w.openWithinDue),
        data.totalOpen,
        data.openDueWithin5Days,
        data.openDue6To8Days,
        data.openPastDueDate
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `postmortem-trends-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading Postmortem Trends...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={fetchPostmortemTrends} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Postmortem Status - Weekly Trends
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label="Last 2 Weeks"
              onClick={() => setSelectedWeeks(2)}
              color={selectedWeeks === 2 ? 'primary' : 'default'}
              variant={selectedWeeks === 2 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Last 3 Weeks"
              onClick={() => setSelectedWeeks(3)}
              color={selectedWeeks === 3 ? 'primary' : 'default'}
              variant={selectedWeeks === 3 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="Last 4 Weeks"
              onClick={() => setSelectedWeeks(4)}
              color={selectedWeeks === 4 ? 'primary' : 'default'}
              variant={selectedWeeks === 4 ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={handleExportToCSV}
          sx={{
            textTransform: 'none',
            bgcolor: '#1976d2',
            '&:hover': { bgcolor: '#1565c0' }
          }}
        >
          Export to CSV
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 650, '& .MuiTableCell-root': { border: '1px solid #000' } }} size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003d82', borderBottom: '2px solid #000' }}>
              <TableCell 
                rowSpan={2} 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold', 
                  borderRight: '2px solid #000',
                  border: '2px solid #000',
                  minWidth: 200
                }}
              >
                Portfolio/Platform
              </TableCell>
              <TableCell 
                colSpan={weeks.length} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '2px solid #000',
                  border: '2px solid #000'
                }}
              >
                Closed
              </TableCell>
              <TableCell 
                colSpan={weeks.length} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '2px solid #000',
                  border: '2px solid #000'
                }}
              >
                Open Post-Mortem within due date
              </TableCell>
              <TableCell 
                colSpan={3} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  border: '2px solid #000'
                }}
              >
                Total Number of Post-Mortem open
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#003d82', borderBottom: '2px solid #000' }}>
              {weeks.map((week, index) => (
                <TableCell 
                  key={`closed-${index}`}
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    border: '2px solid #000',
                    borderRight: '2px solid #000'
                  }}
                >
                  {week}
                </TableCell>
              ))}
              {weeks.map((week, index) => (
                <TableCell 
                  key={`open-${index}`}
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 'bold',
                    border: '2px solid #000',
                    borderRight: '2px solid #000'
                  }}
                >
                  {week}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', border: '2px solid #000' }}>
                Open PM - SLA breach within 5 days
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', border: '2px solid #000' }}>
                Open PM - SLA breach after 5 days
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', border: '2px solid #000', bgcolor: '#c62828' }}>
                Open PM - SLA breached (&gt; 8 days)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trendData.map((row, rowIndex) => {
              const isGrandTotal = row.portfolioPlatform === 'Grand Total';
              const rowBgColor = isGrandTotal ? '#f5f5f5' : 'white';
              
              return (
                <TableRow 
                  key={rowIndex}
                  sx={{ 
                    bgcolor: rowBgColor,
                    '&:hover': !isGrandTotal ? { bgcolor: '#f9f9f9' } : {},
                    borderTop: isGrandTotal ? '2px solid #003d82' : 'none'
                  }}
                >
                  <TableCell 
                    sx={{ 
                      fontWeight: isGrandTotal ? 'bold' : 'normal',
                      borderRight: '2px solid #000',
                      border: '1px solid #000'
                    }}
                  >
                    {row.portfolioPlatform}
                  </TableCell>
                  {row.weeks.map((week, weekIndex) => (
                    <TableCell 
                      key={`closed-${weekIndex}`}
                      align="center"
                      onClick={() => week.closed > 0 && handleCountClick(row.portfolioPlatform, weekIndex, 'closed')}
                      sx={{ 
                        border: '1px solid #000',
                        borderRight: weekIndex === row.weeks.length - 1 ? '2px solid #000' : '1px solid #000',
                        cursor: week.closed > 0 && !isGrandTotal ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        '&:hover': week.closed > 0 && !isGrandTotal ? {
                          backgroundColor: '#f5f5f5'
                        } : {}
                      }}
                    >
                      {week.closed || ''}
                    </TableCell>
                  ))}
                  {row.weeks.map((week, weekIndex) => (
                    <TableCell 
                      key={`open-${weekIndex}`}
                      align="center"
                      onClick={() => week.openWithinDue > 0 && handleCountClick(row.portfolioPlatform, weekIndex, 'open')}
                      sx={{ 
                        border: '1px solid #000',
                        borderRight: weekIndex === row.weeks.length - 1 ? '2px solid #000' : '1px solid #000',
                        cursor: week.openWithinDue > 0 && !isGrandTotal ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        '&:hover': week.openWithinDue > 0 && !isGrandTotal ? {
                          backgroundColor: '#f5f5f5'
                        } : {}
                      }}
                    >
                      {week.openWithinDue || ''}
                    </TableCell>
                  ))}
                  <TableCell 
                    align="center"
                    onClick={() => row.openDueWithin5Days > 0 && handleCountClick(row.portfolioPlatform, 0, 'dueWithin5')}
                    sx={{
                      border: '1px solid #000',
                      bgcolor: (isGrandTotal || row.openDueWithin5Days === 0) ? 'inherit' : '#fff3e0',
                      cursor: row.openDueWithin5Days > 0 && !isGrandTotal ? 'pointer' : 'default',
                      color: isGrandTotal ? 'inherit' : (row.openDueWithin5Days > 0 ? '#e65100' : 'inherit'),
                      fontWeight: 'bold',
                      '&:hover': row.openDueWithin5Days > 0 && !isGrandTotal ? {
                        backgroundColor: '#ffe0b2'
                      } : {}
                    }}
                  >
                    {row.openDueWithin5Days || ''}
                  </TableCell>
                  <TableCell 
                    align="center"
                    onClick={() => row.openDue6To8Days > 0 && handleCountClick(row.portfolioPlatform, 0, 'due6To8')}
                    sx={{
                      border: '1px solid #000',
                      bgcolor: (isGrandTotal || row.openDue6To8Days === 0) ? 'inherit' : '#d4edda',
                      cursor: row.openDue6To8Days > 0 && !isGrandTotal ? 'pointer' : 'default',
                      color: isGrandTotal ? 'inherit' : (row.openDue6To8Days > 0 ? '#2e7d32' : 'inherit'),
                      fontWeight: 'bold',
                      '&:hover': row.openDue6To8Days > 0 && !isGrandTotal ? {
                        backgroundColor: '#c8e6c9'
                      } : {}
                    }}
                  >
                    {row.openDue6To8Days || ''}
                  </TableCell>
                  <TableCell 
                    align="center"
                    onClick={() => row.openPastDueDate > 0 && handleCountClick(row.portfolioPlatform, 0, 'pastDue')}
                    sx={{
                      border: '1px solid #000',
                      bgcolor: (isGrandTotal || row.openPastDueDate === 0) ? 'inherit' : '#ffebee',
                      cursor: row.openPastDueDate > 0 && !isGrandTotal ? 'pointer' : 'default',
                      color: isGrandTotal ? 'inherit' : (row.openPastDueDate > 0 ? '#c62828' : 'inherit'),
                      fontWeight: 'bold',
                      '&:hover': row.openPastDueDate > 0 && !isGrandTotal ? {
                        backgroundColor: '#ffcdd2'
                      } : {}
                    }}
                  >
                    {row.openPastDueDate || ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
          Total Number of Post-Mortem Open:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 0.5 }} />
            <Typography variant="body2">
              <strong>Open PM - SLA breach within 5 days:</strong> Postmortems that will breach 8-day SLA within the next 5 days
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 0.5 }} />
            <Typography variant="body2">
              <strong>Open PM - SLA breach after 5 days:</strong> Postmortems that will breach 8-day SLA after 5 days
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 0.5 }} />
            <Typography variant="body2">
              <strong>Open PM - SLA breached (&gt; 8 days):</strong> Postmortems that have already breached the 8-day SLA
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'white' }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {dialogTitle}
          <Button onClick={handleCloseDialog} sx={{ color: 'white', minWidth: 'auto' }}>
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, bgcolor: 'white' }}>
          {dialogData.length === 0 ? (
            <Alert severity="info">No postmortems found</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Incident ID</strong></TableCell>
                    <TableCell><strong>Created On</strong></TableCell>
                    <TableCell><strong>Impacted App</strong></TableCell>
                    <TableCell><strong>Causing Portfolio</strong></TableCell>
                    <TableCell><strong>Causing App</strong></TableCell>
                    <TableCell><strong>Causing Platform</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Priority</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogData.map((pm: any, index: number) => {
                    // Extract incident number
                    const extractIncidentNumber = (idField: string) => {
                      if (!idField) return null;
                      if (idField.includes('service-now.com') || idField.includes('incidentID=')) {
                        const match = idField.match(/(?:number=|incidentID=)([A-Z0-9]+)/);
                        return match ? match[1] : idField;
                      }
                      if (idField.match(/^[A-Z]{3}\d+$/)) {
                        return idField;
                      }
                      return idField;
                    };

                    const incidentNumber = extractIncidentNumber(pm.incidentID);
                    const postmortemLink = pm.customID || null;

                    return (
                      <TableRow key={index} hover>
                        <TableCell>
                          {incidentNumber && postmortemLink ? (
                            <Link
                              href={postmortemLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                color: '#00A8CC',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {incidentNumber}
                            </Link>
                          ) : (incidentNumber || 'N/A')}
                        </TableCell>
                        <TableCell>
                          {pm.createdOn ? new Date(pm.createdOn).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>{pm.cmdb_ci || 'N/A'}</TableCell>
                        <TableCell>{pm.causedBy_portfolio || 'N/A'}</TableCell>
                        <TableCell>{pm.causedBy_application || 'N/A'}</TableCell>
                        <TableCell>{pm.causedBy_platform || 'N/A'}</TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          {pm.description || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`P${pm.priority}`} 
                            color={pm.priority === '1' ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={pm.incidentStatus || 'N/A'} 
                            size="small"
                            sx={{
                              backgroundColor: (() => {
                                const status = pm.incidentStatus?.toUpperCase().replace(/\s+/g, '');
                                if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED' || status === 'LTREVIEW') return '#d4edda !important'; // Green
                                if (status === 'SREREVIEW' || status === 'SREREVIEWING') return '#fff9c4 !important'; // Light yellow
                                if (status === 'PROGRESS' || status === 'INPROGRESS') return '#ffe0b2 !important'; // Light orange
                                if (status === 'NEW') return '#ffcdd2 !important'; // Light red
                                return '#f5f5f5 !important'; // Gray for others
                              })(),
                              color: (() => {
                                const status = pm.incidentStatus?.toUpperCase().replace(/\s+/g, '');
                                if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED' || status === 'LTREVIEW') return '#155724 !important'; // Dark green
                                if (status === 'SREREVIEW' || status === 'SREREVIEWING') return '#f57f17 !important'; // Dark yellow
                                if (status === 'PROGRESS' || status === 'INPROGRESS') return '#e65100 !important'; // Dark orange
                                if (status === 'NEW') return '#c62828 !important'; // Dark red
                                return '#666 !important'; // Gray text for others
                              })(),
                              fontWeight: 600,
                              '& .MuiChip-label': {
                                color: (() => {
                                  const status = pm.incidentStatus?.toUpperCase().replace(/\s+/g, '');
                                  if (status === 'DONE' || status === 'COMPLETED' || status === 'CLOSED' || status === 'LTREVIEW') return '#155724'; // Dark green
                                  if (status === 'SREREVIEW' || status === 'SREREVIEWING') return '#f57f17'; // Dark yellow
                                  if (status === 'PROGRESS' || status === 'INPROGRESS') return '#e65100'; // Dark orange
                                  if (status === 'NEW') return '#c62828'; // Dark red
                                  return '#666'; // Gray text for others
                                })()
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'white' }}>
          <Button onClick={handleCloseDialog} variant="contained" sx={{ bgcolor: '#1976d2' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PostmortemTrends;
